import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, Search, Loader2, ImageOff, Star } from 'lucide-react';
import { toast } from 'sonner';
import { collateralApi } from '@/api/collateral';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/**
 * Browse-or-upload picker over the shared media library, for collateral image
 * layers. Selecting (or uploading) an image calls onSelect with its /uploads/
 * URL — which the renderer reads straight off disk.
 */
export default function CollateralImagePicker({ open, onOpenChange, onSelect }) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const [brandOnly, setBrandOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async (search, brand) => {
    setLoading(true);
    try {
      const { items: found } = await collateralApi.listImages({
        search, limit: 40, tags: brand ? ['brand'] : null,
      });
      setItems(found);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load(debouncedQuery.trim(), brandOnly);
  }, [open, debouncedQuery, brandOnly, load]);

  const choose = (url) => {
    onSelect(url);
    onOpenChange(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setUploading(true);
    try {
      const media = await collateralApi.uploadImage(file);
      if (!media?.url) throw new Error('no url');
      toast.success('Uploaded');
      choose(media.url); // auto-select the just-uploaded image
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="image-picker">
        <DialogHeader>
          <DialogTitle>Choose an image</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8 h-9"
              placeholder="Search the media library…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="image-picker-search"
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            data-testid="image-picker-upload-input"
          />
          <Button
            type="button"
            variant={brandOnly ? 'default' : 'outline'}
            onClick={() => setBrandOnly((v) => !v)}
            data-testid="image-picker-brand"
            title="Show brand assets (logos) only"
          >
            <Star className={`h-4 w-4 mr-1.5 ${brandOnly ? 'fill-current' : ''}`} /> Brand
          </Button>
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="image-picker-upload"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            Upload
          </Button>
        </div>

        <div className="min-h-[260px] max-h-[50vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-[240px] text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[240px] text-muted-foreground gap-2">
              <ImageOff className="h-7 w-7 opacity-40" />
              <p className="text-sm">No images{query ? ' match your search' : ' yet'} — upload one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
              {items.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => choose(m.url)}
                  data-testid={`image-option-${m.id}`}
                  title={m.original_filename || m.filename}
                  className="aspect-square rounded-md border bg-muted/40 overflow-hidden hover:border-primary hover:ring-1 hover:ring-primary"
                >
                  <img src={m.url} alt={m.alt_text || ''} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
